"use client";

import { useRef, useState, useEffect, useCallback, useId } from "react";
import { motion, AnimatePresence } from "motion/react";
import worldMapDotsUrl from "@/assets/world-map-dots.svg?url";

interface Connection {
  start: { lat: number; lng: number };
  end: { lat: number; lng: number };
}

interface MapProps {
  lineColor?: string;
  /** Show animated connection lines */
  showConnections?: boolean;
  /** Max connections visible at once (when showConnections is true) */
  maxVisible?: number;
  /** Seconds between adding/removing a connection */
  staggerInterval?: number;
  /** Fade edges vertically */
  fade?: boolean;
}

const CONNECTIONS: Connection[] = [
  { start: { lat: 51.5, lng: -0.13 }, end: { lat: 19.08, lng: 72.88 } },
  { start: { lat: 40.71, lng: -74.01 }, end: { lat: 14.6, lng: 120.98 } },
  { start: { lat: 51.5, lng: -0.13 }, end: { lat: -1.29, lng: 36.82 } },
  { start: { lat: 34.05, lng: -118.24 }, end: { lat: -23.55, lng: -46.63 } },
  { start: { lat: 48.86, lng: 2.35 }, end: { lat: 35.68, lng: 139.65 } },
  { start: { lat: 43.65, lng: -79.38 }, end: { lat: 6.52, lng: 3.38 } },
  { start: { lat: -33.87, lng: 151.21 }, end: { lat: 31.23, lng: 121.47 } },
  { start: { lat: 29.76, lng: -95.37 }, end: { lat: 19.43, lng: -99.13 } },
  { start: { lat: 52.52, lng: 13.41 }, end: { lat: -33.93, lng: 18.42 } },
  { start: { lat: 40.42, lng: -3.7 }, end: { lat: -34.6, lng: -58.38 } },
  { start: { lat: 40.71, lng: -74.01 }, end: { lat: 51.5, lng: -0.13 } },
  { start: { lat: 25.2, lng: 55.27 }, end: { lat: 24.86, lng: 67.01 } },
  { start: { lat: 1.35, lng: 103.82 }, end: { lat: -33.87, lng: 151.21 } },
  { start: { lat: 41.9, lng: 12.5 }, end: { lat: 30.04, lng: 31.24 } },
  { start: { lat: 37.77, lng: -122.42 }, end: { lat: 37.57, lng: 126.98 } },
];

export default function WorldMap({
  lineColor = "#29a383",
  showConnections = true,
  maxVisible = 4,
  staggerInterval = 2.5,
  fade = false,
}: MapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const nextIndexRef = useRef(maxVisible);
  const gradientId = useId();

  const [activeSlots, setActiveSlots] = useState<Array<{ connIndex: number; key: number }>>(() =>
    Array.from({ length: maxVisible }, (_, i) => ({ connIndex: i, key: i }))
  );

  const cycleOne = useCallback(() => {
    setActiveSlots((prev) => {
      const slotToReplace = Math.floor(Math.random() * prev.length);
      const next = [...prev];
      const newIndex = nextIndexRef.current % CONNECTIONS.length;
      nextIndexRef.current++;
      next[slotToReplace] = { connIndex: newIndex, key: nextIndexRef.current };
      return next;
    });
  }, []);

  useEffect(() => {
    if (!showConnections) return;
    const interval = setInterval(cycleOne, staggerInterval * 1000);
    return () => clearInterval(interval);
  }, [cycleOne, staggerInterval, showConnections]);

  const projectPoint = (lat: number, lng: number) => {
    const x = (lng + 180) * (800 / 360);
    const y = (90 - lat) * (400 / 180);
    return { x, y };
  };

  const createCurvedPath = (
    start: { x: number; y: number },
    end: { x: number; y: number }
  ) => {
    const midX = (start.x + end.x) / 2;
    const midY = Math.min(start.y, end.y) - 50;
    return `M ${start.x} ${start.y} Q ${midX} ${midY} ${end.x} ${end.y}`;
  };

  const fadeStyle = fade
    ? "[mask-image:linear-gradient(to_bottom,transparent,white_15%,white_85%,transparent)] [-webkit-mask-image:linear-gradient(to_bottom,transparent,white_15%,white_85%,transparent)]"
    : "";

  return (
    <div className={`w-full aspect-[2/1] relative font-sans ${fadeStyle}`}>
      <div
        className="h-full w-full pointer-events-none select-none"
        aria-hidden="true"
        style={{
          background: "linear-gradient(135deg, #29a38330 0%, #29a38360 50%, #29a38320 100%)",
          maskImage: `url("${worldMapDotsUrl}")`,
          WebkitMaskImage: `url("${worldMapDotsUrl}")`,
          maskSize: "cover",
          WebkitMaskSize: "cover",
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
        }}
      />
      {showConnections && (
        <svg
          ref={svgRef}
          viewBox="0 0 800 400"
          className="w-full h-full absolute inset-0 pointer-events-none select-none"
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="white" stopOpacity="0" />
              <stop offset="5%" stopColor={lineColor} stopOpacity="1" />
              <stop offset="95%" stopColor={lineColor} stopOpacity="1" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
          </defs>

          <AnimatePresence>
            {activeSlots.map((slot) => {
              const conn = CONNECTIONS[slot.connIndex];
              const startPoint = projectPoint(conn.start.lat, conn.start.lng);
              const endPoint = projectPoint(conn.end.lat, conn.end.lng);
              return (
                <motion.g
                  key={slot.key}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1 }}
                >
                  <motion.path
                    d={createCurvedPath(startPoint, endPoint)}
                    fill="none"
                    stroke={`url(#${gradientId})`}
                    strokeWidth="1"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                  />
                  <circle cx={startPoint.x} cy={startPoint.y} r="2" fill={lineColor} />
                  <circle cx={startPoint.x} cy={startPoint.y} r="2" fill={lineColor} opacity="0.5">
                    <animate attributeName="r" from="2" to="8" dur="1.5s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.5" to="0" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                  <circle cx={endPoint.x} cy={endPoint.y} r="2" fill={lineColor} />
                  <circle cx={endPoint.x} cy={endPoint.y} r="2" fill={lineColor} opacity="0.5">
                    <animate attributeName="r" from="2" to="8" dur="1.5s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.5" to="0" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                </motion.g>
              );
            })}
          </AnimatePresence>
        </svg>
      )}
    </div>
  );
}
